import type { Vec2 } from './vec';

/**
 * Computes the signed area of a polygon using the shoelace formula.
 * Positive area indicates counterclockwise vertex ordering.
 *
 * Formula: area = 0.5 * sum(x_i * y_{i+1} - x_{i+1} * y_i) for all vertices,
 * wrapping the last vertex to the first.
 *
 * @param points - Array of vertices defining the polygon
 * @returns Signed area; positive for CCW, negative for CW
 */
export function shoelaceArea(points: readonly Vec2[]): number {
  if (points.length < 3) {
    return 0;
  }

  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const curr = points[i]!;
    const next = points[(i + 1) % points.length]!;
    sum += curr.x * next.y - next.x * curr.y;
  }

  return sum / 2;
}

/**
 * Generates vertices of a regular polygon (equal sides, equal angles).
 *
 * Vertices are evenly spaced on a circle of radius r, starting at angle 0 (positive x-axis)
 * and proceeding counterclockwise with angular spacing 2π/n.
 *
 * @param n - Number of vertices
 * @param r - Radius (distance from center to each vertex)
 * @param center - Center point; defaults to (0, 0)
 * @returns Array of n vertices ordered counterclockwise
 */
export function regularPolygonVertices(
  n: number,
  r: number,
  center: Vec2 = { x: 0, y: 0 },
): Vec2[] {
  const vertices: Vec2[] = [];
  const angleStep = (2 * Math.PI) / n;

  for (let i = 0; i < n; i++) {
    const angle = i * angleStep;
    const x = center.x + r * Math.cos(angle);
    const y = center.y + r * Math.sin(angle);
    vertices.push({ x, y });
  }

  return vertices;
}

/**
 * Interface describing a composite decomposition of a rectilinear polygon.
 */
export interface CompositeDecomposition {
  /** Array of axis-aligned rectangular parts, each represented as a 4-vertex polygon */
  parts: Vec2[][];
  /** Internal slice segments representing the cuts between parts */
  cuts: [Vec2, Vec2][];
}

/**
 * Decomposes a rectilinear (axis-aligned) polygon into axis-aligned rectangular parts.
 *
 * This function uses a horizontal sweep algorithm:
 * 1. Collect all distinct y-values from the polygon vertices.
 * 2. Sort them and sweep horizontally through successive y-bands.
 * 3. For each band, determine the x-extent of the figure and emit an axis-aligned rectangle.
 * 4. Record horizontal slice lines between bands as cuts.
 *
 * **Important:** This function handles ONLY rectilinear polygons (all edges are horizontal or vertical,
 * with vertices at axis-aligned corners). For non-rectilinear input, behavior is undefined.
 *
 * @param figure - Vertices of a rectilinear polygon, ordered consistently (CCW or CW)
 * @returns CompositeDecomposition with rectangular parts and internal cuts
 */
export function compositeDecompose(figure: readonly Vec2[]): CompositeDecomposition {
  if (figure.length < 3) {
    return { parts: [], cuts: [] };
  }

  // Collect all distinct y-values and sort them
  const yValues = new Set(figure.map((p) => p.y));
  const sortedYs = Array.from(yValues).sort((a, b) => a - b);

  if (sortedYs.length < 2) {
    // Degenerate case: all points have same y-coordinate
    return { parts: [], cuts: [] };
  }

  const parts: Vec2[][] = [];
  const cuts: [Vec2, Vec2][] = [];

  // For each horizontal band between consecutive y-values
  for (let i = 0; i < sortedYs.length - 1; i++) {
    const y1 = sortedYs[i]!;
    const y2 = sortedYs[i + 1]!;
    const yMid = (y1 + y2) / 2;

    // Find all x-intervals that are inside the polygon at y-level yMid
    // Collect all x-coordinates of vertical edges that cross yMid
    const xEvents: Array<{ x: number }> = [];

    for (let j = 0; j < figure.length; j++) {
      const p1 = figure[j]!;
      const p2 = figure[(j + 1) % figure.length]!;

      // Only consider vertical edges
      if (Math.abs(p1.x - p2.x) < 1e-9) {
        const minY = Math.min(p1.y, p2.y);
        const maxY = Math.max(p1.y, p2.y);

        // Check if this vertical edge crosses yMid
        if (minY < yMid - 1e-9 && maxY > yMid + 1e-9) {
          xEvents.push({ x: p1.x });
        }
      }
    }

    // Sort by x-coordinate
    xEvents.sort((a, b) => a.x - b.x);

    // Pair consecutive x-coordinates to form rectangles
    for (let j = 0; j < xEvents.length - 1; j += 2) {
      const x1 = xEvents[j]!.x;
      const x2 = xEvents[j + 1]!.x;

      if (x2 - x1 > 1e-9) {
        // Create an axis-aligned rectangle (CCW order)
        const rect: Vec2[] = [
          { x: x1, y: y1 },
          { x: x2, y: y1 },
          { x: x2, y: y2 },
          { x: x1, y: y2 },
        ];
        parts.push(rect);
      }
    }
  }

  // Emit internal cuts: for each interior y-boundary, find the x-overlap
  // between the band below and the band above, and emit the cut segment.
  for (let i = 1; i < sortedYs.length - 1; i++) {
    const yBoundary = sortedYs[i]!;
    const yMidBelow = (sortedYs[i - 1]! + yBoundary) / 2;
    const yMidAbove = (yBoundary + sortedYs[i + 1]!) / 2;

    // Find x-extents for the band below
    const xEventsBelow: number[] = [];
    for (let j = 0; j < figure.length; j++) {
      const p1 = figure[j]!;
      const p2 = figure[(j + 1) % figure.length]!;

      if (Math.abs(p1.x - p2.x) < 1e-9) {
        const minY = Math.min(p1.y, p2.y);
        const maxY = Math.max(p1.y, p2.y);

        if (minY < yMidBelow - 1e-9 && maxY > yMidBelow + 1e-9) {
          xEventsBelow.push(p1.x);
        }
      }
    }
    xEventsBelow.sort((a, b) => a - b);

    // Find x-extents for the band above
    const xEventsAbove: number[] = [];
    for (let j = 0; j < figure.length; j++) {
      const p1 = figure[j]!;
      const p2 = figure[(j + 1) % figure.length]!;

      if (Math.abs(p1.x - p2.x) < 1e-9) {
        const minY = Math.min(p1.y, p2.y);
        const maxY = Math.max(p1.y, p2.y);

        if (minY < yMidAbove - 1e-9 && maxY > yMidAbove + 1e-9) {
          xEventsAbove.push(p1.x);
        }
      }
    }
    xEventsAbove.sort((a, b) => a - b);

    // Extract x-intervals for both bands
    const intervalsBelow: Array<[number, number]> = [];
    for (let j = 0; j < xEventsBelow.length - 1; j += 2) {
      intervalsBelow.push([xEventsBelow[j]!, xEventsBelow[j + 1]!]);
    }

    const intervalsAbove: Array<[number, number]> = [];
    for (let j = 0; j < xEventsAbove.length - 1; j += 2) {
      intervalsAbove.push([xEventsAbove[j]!, xEventsAbove[j + 1]!]);
    }

    // For each pair of overlapping intervals, emit a cut
    for (const [xBelowMin, xBelowMax] of intervalsBelow) {
      for (const [xAboveMin, xAboveMax] of intervalsAbove) {
        const overlapMin = Math.max(xBelowMin, xAboveMin);
        const overlapMax = Math.min(xBelowMax, xAboveMax);

        if (overlapMax - overlapMin > 1e-9) {
          cuts.push([
            { x: overlapMin, y: yBoundary },
            { x: overlapMax, y: yBoundary },
          ]);
        }
      }
    }
  }

  return { parts, cuts };
}
